import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, TriangleAlert, RefreshCw, Check } from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { usePeriodDrilldown } from '../../hooks/usePeriodDrilldown';

// =============================================================================
// Finance › Balance Sheet   (LIVE)
//
// OPENING POSITION + BUDGETED MOVEMENTS.
//
// The opening position is real, not entered: act_gl carries the prior year's
// closing balance sheet accounts, and the view takes those and only those. The
// same ledger also holds the prior year's P&L, and rolling that in as well is
// the mistake this page's whole data path is shaped to avoid — it would leave
// the opening position out by exactly the prior year result, with every
// individual line still looking perfectly reasonable.
//
// THIS IS A DERIVED STATEMENT, NOT A DOUBLE-ENTERED ONE.
// The spine is a single-sided ledger: it holds the P&L posting and not its
// contra. Reconstructing the missing credits would mean modelling every accrual
// and settlement in the business. So each line comes from its own driver —
// debtors from revenue and terms, creditors from purchases and terms, PPE from
// capex and depreciation, retained earnings from cumulative profit — and the
// residual is REPORTED rather than plugged.
//
// WHICH MEANS THE BALANCE CHECK IS THE POINT OF THE PAGE, not a footnote. It
// sits at the top, in red when it is material, and it reads as "how much of the
// budget is not yet explained by a balance sheet driver". A budgeted balance
// sheet that has been quietly forced to balance is worth nothing.
//
// SIGN: balances arrive in presentation sign — assets positive AND equity and
// liabilities positive — so the two totals print positive and the check is that
// they are EQUAL. The flip off the debit-positive ledger happens once,
// server-side. Never flip again here.
//
// GROUP LEVEL, deliberately. Sites do not have their own equity or bank
// accounts, and a per-site balance sheet invites an intercompany argument a
// budgeting product has no business having.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';

const fmtR = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '—';
  const s = Math.abs(v).toLocaleString();
  return v < 0 ? `(${s})` : s;
};

const T_GROUP = { bgCell: 'rgba(100, 116, 139, 0.13)', baseFontStyle: '600 12px' };
const T_SUBTOTAL = { bgCell: 'rgba(37, 99, 235, 0.13)', baseFontStyle: '700 12px' };
const T_GRAND = { bgCell: 'rgba(100, 116, 139, 0.20)', baseFontStyle: '700 13px' };
const T_CHECK_OK = { bgCell: 'rgba(16, 185, 129, 0.18)', baseFontStyle: '700 12px' };
const T_CHECK_BAD = { bgCell: 'rgba(239, 68, 68, 0.18)', baseFontStyle: '700 12px' };
const T_OPENING = { bgCell: 'rgba(100, 116, 139, 0.10)' };

// Anything smaller than this is rounding, not a modelling gap. A balance sheet
// that shouts about R4 is a balance sheet people stop reading.
const MATERIAL = 1000;

const EMPTY = {
  structure: { sections: [], groups: [], subtotals: [] },
  rows: [], opening: [], check: [], unclassified: [],
};

export default function BalanceSheet() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getBalanceSheet();
      setData(res.data || EMPTY);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the balance sheet.');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  const sections = useMemo(
    () => (data.structure?.sections || []).filter((s) => !s.excluded),
    [data.structure]
  );
  const groups = useMemo(() => data.structure?.groups || [], [data.structure]);
  const subtotals = useMemo(() => data.structure?.subtotals || [], [data.structure]);

  // ---- Pivot: section x period ---------------------------------------------
  const bySection = useMemo(() => {
    const m = new Map();
    const ensure = (k) => {
      if (!m.has(k)) m.set(k, { opening: 0, values: Array(12).fill(0) });
      return m.get(k);
    };
    (data.opening || []).forEach((o) => { ensure(o.bs_section).opening += o.balance; });
    (data.rows || []).forEach((r) => {
      if (r.financial_period >= 1 && r.financial_period <= 12) {
        ensure(r.bs_section).values[r.financial_period - 1] += r.balance;
      }
    });
    return m;
  }, [data.opening, data.rows]);

  const checkByPeriod = useMemo(() => {
    const m = new Map();
    (data.check || []).forEach((c) => m.set(c.financial_period, c));
    return m;
  }, [data.check]);

  // ---- Build the statement -------------------------------------------------
  //
  // A subtotal sums its NAMED MEMBERS, not everything above it. A balance sheet
  // is two independent columns that have to match; a cumulative subtotal would
  // add assets to liabilities and produce a number that means nothing.
  const display = useMemo(() => {
    const out = [];
    const zero = { opening: 0, values: Array(12).fill(0) };

    const sumMembers = (members) => {
      const vals = Array(12).fill(0);
      let opening = 0;
      members.forEach((k) => {
        const s = bySection.get(k) || zero;
        opening += s.opening;
        s.values.forEach((v, i) => { vals[i] += v; });
      });
      return { opening, values: vals };
    };

    groups.forEach((g) => {
      const inGroup = sections.filter((s) => s.group === g.key);
      // Only draw a section if it carries a number somewhere. A chart with no
      // intangibles should not have an Intangible Assets line reading nil for
      // twelve months.
      const live = inGroup.filter((s) => {
        const d = bySection.get(s.key);
        return d && (d.opening !== 0 || d.values.some((v) => v !== 0));
      });
      if (!live.length) return;

      out.push({ kind: 'group', key: g.key, label: g.label });
      live.forEach((s) => {
        const d = bySection.get(s.key);
        out.push({
          kind: 'line', key: s.key, label: s.label, hint: s.hint,
          opening: d.opening, values: d.values,
        });
      });

      // Subtotals drawn beneath the last section of this group.
      const lastKey = inGroup[inGroup.length - 1]?.key;
      subtotals.filter((st) => st.after === lastKey).forEach((st) => {
        const d = sumMembers(st.members);
        out.push({
          kind: 'subtotal', key: st.key, label: st.label,
          emphasis: !!st.emphasis, grand: !!st.grand,
          opening: d.opening, values: d.values,
        });
      });
    });

    // The check, as a row of the statement rather than a note under it.
    if (checkByPeriod.size) {
      const vals = Array(12).fill(0);
      PERIODS.forEach((p) => { vals[p - 1] = checkByPeriod.get(p)?.difference || 0; });
      const openA = sumMembers(subtotals.find((s) => s.key === 'TOTAL_ASSETS')?.members || []).opening;
      const openE = sumMembers(subtotals.find((s) => s.key === 'TOTAL_EQUITY_AND_LIABILITIES')?.members || []).opening;
      out.push({
        kind: 'check', key: 'BALANCE_CHECK', label: 'Difference (assets less equity & liabilities)',
        opening: openA - openE, values: vals,
      });
    }

    return out;
  }, [groups, sections, subtotals, bySection, checkByPeriod]);

  // Headline: the worst difference across the year, which is the number that
  // decides whether this statement can be relied on at all.
  const worstDiff = useMemo(() => {
    let worst = { p: null, v: 0 };
    (data.check || []).forEach((c) => {
      if (Math.abs(c.difference) > Math.abs(worst.v)) worst = { p: c.financial_period, v: c.difference };
    });
    return worst.p ? worst : null;
  }, [data.check]);

  const closing = useMemo(() => checkByPeriod.get(12) || null, [checkByPeriod]);
  const balances = !worstDiff || Math.abs(worstDiff.v) < MATERIAL;

  // ---- Columns -------------------------------------------------------------
  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  // Label + Opening. Opening is a permanent column, not a period: every line on
  // this statement is "where it started, then where it went", and hiding the
  // start makes the movement unreadable.
  const FIXED = 2;

  const gcols = useMemo(() => {
    const dyn = visibleCols.map((c) => {
      if (c.kind === 'year') return { id: 'YEAR', title: `${yearExpanded ? '▾' : '▸'} Closing`, width: 132 };
      if (c.kind === 'quarter') return { id: `Q${c.q}`, title: `${expandedQuarters[c.q] ? '▾' : '▸'} Q${c.q}`, width: 112 };
      return { id: `P${c.period}`, title: periodLabel(c.period, fyStartMonth), width: 94 };
    });
    return [
      { id: 'label', title: 'Balance Sheet', width: 300, grow: 1 },
      { id: 'opening', title: 'Opening', width: 124 },
      ...dyn,
      ...(yearExpanded ? [{ id: 'movement', title: 'Movement', width: 124 }] : []),
    ];
  }, [visibleCols, yearExpanded, expandedQuarters, fyStartMonth]);

  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  // EVERY PERIOD COLUMN IS A CLOSING BALANCE, NEVER A SUM.
  // This is the one thing that separates a balance sheet grid from a P&L grid:
  // a quarter column shows the position at the END of the quarter, because
  // adding three closing balances together is meaningless and would look
  // entirely plausible on screen.
  const valueAt = useCallback((r, d) => {
    if (d.kind === 'month') return r.values[d.period - 1] || 0;
    const last = d.periods[d.periods.length - 1];
    return r.values[last - 1] || 0;
  }, []);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = display[row];
    if (!r) return gcell.text('', { readonly: true });

    const material = r.kind === 'check' && Math.abs(r.values[11] || r.opening) >= MATERIAL;
    const theme = r.kind === 'group' ? T_GROUP
      : r.kind === 'check' ? (material ? T_CHECK_BAD : T_CHECK_OK)
        : r.kind === 'subtotal' ? (r.grand ? T_GRAND : T_SUBTOTAL)
          : undefined;

    if (id === 'label') {
      if (r.kind === 'group') return gcell.text(r.label.toUpperCase(), { readonly: true, theme });
      if (r.kind === 'subtotal' || r.kind === 'check') return gcell.text(r.label.toUpperCase(), { readonly: true, theme });
      return gcell.text(`  ${r.label}`, { readonly: true });
    }

    if (id === 'opening') {
      if (r.kind === 'group') return gcell.text('', { readonly: true, theme });
      return gcell.number(r.opening, { display: fmtR(r.opening), readonly: true, theme: theme || T_OPENING });
    }

    if (id === 'movement') {
      // Closing less opening — the only "total" a balance sheet line has that
      // means anything.
      if (r.kind === 'group') return gcell.text('', { readonly: true, theme });
      const mv = (r.values[11] || 0) - r.opening;
      return gcell.number(mv, { display: fmtR(mv), readonly: true, theme: theme || T_OPENING });
    }

    const d = visibleCols[col - FIXED];
    if (!d) return gcell.text('', { readonly: true, theme });
    if (r.kind === 'group') return gcell.text('', { readonly: true, theme });

    const v = valueAt(r, d);
    return gcell.number(v, { display: fmtR(v), readonly: true, theme });
  }, [colIds, display, visibleCols, FIXED, valueAt]);

  const onHeaderClicked = useCallback((colIndex) => {
    const id = colIds[colIndex];
    if (id === 'YEAR') toggleYear();
    else if (/^Q\d+$/.test(id)) toggleQuarter(Number(id.slice(1)));
  }, [colIds, toggleYear, toggleQuarter]);

  // ---- Issues --------------------------------------------------------------
  const issues = useMemo(() => {
    const out = [];
    if (error) {
      out.push({ id: 'load', level: 'error', title: 'The balance sheet could not be loaded', body: error });
    }

    if (worstDiff && Math.abs(worstDiff.v) >= MATERIAL) {
      const assets = checkByPeriod.get(worstDiff.p)?.total_assets || 0;
      const pct = assets ? Math.abs(worstDiff.v / assets) * 100 : 0;
      out.push({
        id: 'balance', level: pct > 5 ? 'error' : 'warn',
        title: `The statement is out by ${fmtR(Math.abs(worstDiff.v))} at its worst, in ${periodLabel(worstDiff.p, fyStartMonth)}`,
        body: `That is ${pct.toFixed(1)}% of total assets. Every line here is derived from its own driver rather than double-entered, so a residual is expected — it is the part of the budget no balance sheet driver yet explains. It is shown, never plugged.`,
        fix: 'The usual causes, in order: budgeted cost with no GL account (check the P&L), working capital assumptions not set, or a movement that has a P&L effect but no matching balance sheet driver.',
      });
    }

    if ((data.unclassified || []).length > 0) {
      const n = data.unclassified.length;
      out.push({
        id: 'unclassified', level: 'warn',
        title: `${n} account${n === 1 ? ' has' : 's have'} no balance sheet section set`,
        body: `${n === 1 ? 'It has' : 'They have'} been placed by account category — a guess, and it moves the subtotals.`,
        detail: data.unclassified.slice(0, 8)
          .map((u) => `${u.account_code} ${u.account_description} → ${u.fallback_section}`).join(' · ')
          + (n > 8 ? ` · +${n - 8} more` : ''),
        fix: 'Set them in Master Data › GL Accounts — the Balance Sheet Section column.',
      });
    }

    // Inventory below zero is not a rounding artefact — it means the plan sells
    // more than it makes, and it is invisible on the P&L.
    const inv = bySection.get('INVENTORY');
    if (inv && inv.values.some((v) => v < 0)) {
      const worst = Math.min(...inv.values);
      out.push({
        id: 'neg-stock', level: 'error',
        title: `Inventory goes negative (${fmtR(worst)} at its lowest)`,
        body: 'Closing stock is opening plus produced less sold, at cost. A negative balance means the budget sells more units than it produces — which the P&L cannot show you, because revenue is booked on sales and cost only on production.',
        fix: 'Raise production volumes to cover the sales plan, or lower the sales plan. Manufacturing › Budget Setup › Volumes.',
      });
    }

    return out;
  }, [error, worstDiff, checkByPeriod, data.unclassified, bySection, fyStartMonth]);

  const worstLevel = issues.some((i) => i.level === 'error') ? 'error' : issues.length ? 'warn' : null;
  const issuesRef = useRef(null);
  const goToIssues = () => issuesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to see the balance sheet.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/finance')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Finance
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              Balance Sheet
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
              Group
            </span>
            {worstLevel && (
              <button
                onClick={goToIssues}
                title={issues.map((i) => i.title).join('\n')}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold transition-colors ${
                  worstLevel === 'error'
                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300'}`}
              >
                <TriangleAlert className="w-3 h-3" />
                {issues.length} {issues.length === 1 ? 'issue' : 'issues'}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!loading && closing && (
              <>
                <Figure label="Total assets" value={fmtR(closing.total_assets)} />
                <Figure label="Equity & liabilities" value={fmtR(closing.total_equity_and_liabilities)} />
                {/* THE BALANCE CHECK IS A HEADLINE, not a footnote. It is the
                    first thing a reader needs and the last thing they would
                    scroll to find. */}
                <Figure
                  label={balances ? 'Balanced' : 'Out of balance'}
                  value={balances ? '✓' : fmtR(Math.abs(worstDiff.v))}
                  sub={balances ? '' : `worst: ${periodLabel(worstDiff.p, fyStartMonth)}`}
                  tone={balances ? 'good' : 'bad'}
                />
              </>
            )}
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : error ? (
            <div className="py-10 px-6 text-center">
              <p className="m-0 text-sm font-semibold text-red-700 dark:text-red-300">The balance sheet could not be loaded.</p>
              <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">{error}</p>
              <button className={`${btn} mt-4`} onClick={load}>
                <RefreshCw className="w-3.5 h-3.5" /> Try again
              </button>
            </div>
          ) : display.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              No opening position for {selectedVersion}. The balance sheet opens on the prior year&rsquo;s closing
              balances in <code className="mx-0.5">act_gl</code> — if the actuals import carries no balance sheet
              accounts, enter them in <code className="mx-0.5">bud_opening_balances</code> instead.
            </p>
          ) : (
            <MantleDataGrid
              gridId="finance-balancesheet"
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={display.length}
              getCellContent={getCellContent}
              onHeaderClicked={onHeaderClicked}
              freezeColumns={2}
              hint="Click Closing → quarters → months to drill into the periods. Every period column is a CLOSING POSITION, not a total — balances are never summed across periods."
              toolbar={
                <>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    assets and equity &amp; liabilities both shown positive · balances, not movements
                  </span>
                  <button type="button" onClick={yearExpanded ? collapseAll : expandAll} className={btn}>
                    {yearExpanded ? 'Collapse periods' : 'Expand periods'}
                  </button>
                </>
              }
            />
          )}
        </div>

        <div ref={issuesRef} className="scroll-mt-2 space-y-3">
          {issues.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                <TriangleAlert className={`w-4 h-4 ${worstLevel === 'error' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {issues.length} thing{issues.length === 1 ? '' : 's'} to fix before this statement is final
                </h2>
              </div>
              <ul className="m-0 p-0 list-none divide-y divide-slate-100 dark:divide-slate-700/60">
                {issues.map((i) => (
                  <li key={i.id} className={`px-4 py-2.5 border-l-4 ${
                    i.level === 'error' ? 'border-l-red-500 bg-red-50/40 dark:bg-red-500/5'
                      : 'border-l-amber-400 bg-amber-50/40 dark:bg-amber-500/5'}`}>
                    <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">{i.title}</p>
                    <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">{i.body}</p>
                    {i.detail && (
                      <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400 break-words">{i.detail}</p>
                    )}
                    {i.fix && <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">{i.fix}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {balances && !loading && display.length > 0 && (
            <p className="m-0 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
              <Check className="w-3.5 h-3.5" />
              Assets equal equity and liabilities in every period.
            </p>
          )}

          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>Opening position.</strong> The prior year&rsquo;s closing balances on asset, liability and equity
            accounts, taken from <code className="mx-0.5">act_gl</code>. The prior year&rsquo;s profit and loss is
            deliberately excluded — retained earnings brought forward already contains it, and adding it again would
            put the opening position out by exactly that amount.
          </p>
          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>Derived, not double-entered.</strong> Each line comes from its own driver: PPE from capex and
            depreciation, inventory from production and sales at cost, debtors and creditors from revenue, purchases
            and the days each counterparty actually settles in, retained earnings from cumulative profit after tax.
            Sections come from each account&rsquo;s <code className="mx-0.5">bs_section</code>, set in Master Data
            &rsaquo; GL Accounts. Every period column is a closing position; balances are never summed across periods.
          </p>
        </div>
      </div>
    </div>
  );
}

const Figure = ({ label, value, sub, tone }) => {
  const cls = tone === 'bad'
    ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
    : tone === 'good'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';
  return (
    <div className={`inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border ${cls}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
      <span className="text-sm font-bold tabular-nums">{value}</span>
      {sub && <span className="text-[10px] opacity-70">{sub}</span>}
    </div>
  );
};
